/**
 * Vuex module — Chatbot (KAN-185 Phase 1).
 *
 * Owns the sidebar's open/closed state, the active conversation, the user's
 * session list, and the in-flight loading flag. All HTTP calls go through the
 * global axios instance (auto-configured with the bearer token in bootstrap.js).
 *
 * Sessions paginate at 20/page on the backend; we keep the latest page only —
 * the sidebar list will lazy-load older sessions in a future phase.
 */

const namespaced = true;

const state = () => ({
    sessions: [],
    activeSessionId: null,
    messages: [],
    isLoading: false,
    isSidebarOpen: false,
    error: null,
});

const getters = {
    activeSession: (state) =>
        state.sessions.find((s) => s.id === state.activeSessionId) || null,

    hasMessages: (state) => state.messages.length > 0,
};

const mutations = {
    SET_SESSIONS(state, sessions) {
        state.sessions = sessions || [];
    },
    PREPEND_SESSION(state, session) {
        // New chats land at the top of the list.
        state.sessions = [session, ...state.sessions.filter((s) => s.id !== session.id)];
    },
    REMOVE_SESSION(state, sessionId) {
        state.sessions = state.sessions.filter((s) => s.id !== sessionId);
        if (state.activeSessionId === sessionId) {
            state.activeSessionId = null;
            state.messages = [];
        }
    },
    SET_ACTIVE_SESSION(state, sessionId) {
        state.activeSessionId = sessionId;
    },
    SET_MESSAGES(state, messages) {
        state.messages = messages || [];
    },
    APPEND_MESSAGE(state, message) {
        state.messages.push(message);
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = !!isLoading;
    },
    SET_SIDEBAR_OPEN(state, open) {
        state.isSidebarOpen = !!open;
    },
    TOGGLE_SIDEBAR(state) {
        state.isSidebarOpen = !state.isSidebarOpen;
    },
    SET_ERROR(state, err) {
        state.error = err;
    },
    BUMP_SESSION_TIMESTAMP(state, sessionId) {
        // Move the touched session to the top of the list (mirrors backend `touch`).
        const idx = state.sessions.findIndex((s) => s.id === sessionId);
        if (idx > 0) {
            const [s] = state.sessions.splice(idx, 1);
            s.updated_at = new Date().toISOString();
            state.sessions = [s, ...state.sessions];
        }
    },
};

const actions = {
    async fetchSessions({ commit }) {
        commit('SET_ERROR', null);
        try {
            const { data } = await window.axios.get('/api/chat/sessions');
            // Backend returns Laravel paginated response: { data: [...], current_page, ... }
            commit('SET_SESSIONS', data.data || []);
        } catch (err) {
            commit('SET_ERROR', extractError(err));
        }
    },

    async createSession({ commit }, title = null) {
        commit('SET_ERROR', null);
        try {
            const { data } = await window.axios.post('/api/chat/sessions', { title });
            commit('PREPEND_SESSION', data);
            commit('SET_ACTIVE_SESSION', data.id);
            commit('SET_MESSAGES', []);
            return data;
        } catch (err) {
            commit('SET_ERROR', extractError(err));
            throw err;
        }
    },

    async loadSession({ commit }, sessionId) {
        commit('SET_ERROR', null);
        commit('SET_LOADING', true);
        try {
            const { data } = await window.axios.get(`/api/chat/sessions/${sessionId}`);
            commit('SET_ACTIVE_SESSION', data.id);
            commit('SET_MESSAGES', data.messages || []);
        } catch (err) {
            commit('SET_ERROR', extractError(err));
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async deleteSession({ commit }, sessionId) {
        commit('SET_ERROR', null);
        try {
            await window.axios.delete(`/api/chat/sessions/${sessionId}`);
            commit('REMOVE_SESSION', sessionId);
        } catch (err) {
            commit('SET_ERROR', extractError(err));
            throw err;
        }
    },

    /**
     * Send a user message. The backend creates a new session if none is active.
     * We optimistically append the user bubble first so the UI updates instantly,
     * then append the assistant reply on success.
     */
    async sendMessage({ commit, state }, message) {
        const trimmed = (message || '').trim();
        if (!trimmed || state.isLoading) return;

        commit('SET_ERROR', null);
        commit('APPEND_MESSAGE', {
            id: `local-${Date.now()}`,
            role: 'user',
            content: trimmed,
            optimistic: true,
        });
        commit('SET_LOADING', true);

        try {
            const payload = { message: trimmed };
            if (state.activeSessionId) {
                payload.session_id = state.activeSessionId;
            }
            const { data } = await window.axios.post('/api/chat/query', payload);

            // Replace any optimistic user bubble with the canonical pair from the server.
            // The backend doesn't echo the user message id, so we keep the optimistic id
            // and just remove its `optimistic` flag — the next session reload will reconcile.
            commit('APPEND_MESSAGE', {
                id: data.message_id,
                role: 'assistant',
                content: data.response,
                metadata: { intent: data.intent },
            });
            commit('SET_ACTIVE_SESSION', data.session_id);

            // If this session isn't in the list yet (new chat that the backend
            // created implicitly), prepend it so the rail updates immediately.
            const alreadyListed = state.sessions.some((s) => s.id === data.session_id);
            if (!alreadyListed) {
                commit('PREPEND_SESSION', {
                    id: data.session_id,
                    title: trimmed.slice(0, 50),
                    updated_at: new Date().toISOString(),
                });
            } else {
                commit('BUMP_SESSION_TIMESTAMP', data.session_id);
            }
        } catch (err) {
            // Roll back the optimistic user bubble so the UI doesn't lie about "sent".
            commit('SET_MESSAGES', state.messages.filter((m) => !m.optimistic));
            commit('SET_ERROR', extractError(err));
        } finally {
            commit('SET_LOADING', false);
        }
    },

    openSidebar({ commit, state, dispatch }) {
        commit('SET_SIDEBAR_OPEN', true);
        // Lazy-load sessions on first open.
        if (state.sessions.length === 0) {
            dispatch('fetchSessions');
        }
    },

    closeSidebar({ commit }) {
        commit('SET_SIDEBAR_OPEN', false);
    },

    toggleSidebar({ commit, state, dispatch }) {
        if (!state.isSidebarOpen && state.sessions.length === 0) {
            dispatch('fetchSessions');
        }
        commit('TOGGLE_SIDEBAR');
    },

    startNewChat({ commit }) {
        commit('SET_ACTIVE_SESSION', null);
        commit('SET_MESSAGES', []);
        commit('SET_ERROR', null);
    },
};

/**
 * Pull a user-friendly message out of an axios error.
 */
function extractError(err) {
    if (err && err.response) {
        const data = err.response.data;
        if (typeof data === 'string') return data;
        if (data && data.message) return data.message;
        if (err.response.status === 403) return 'The chatbot is not enabled for your account.';
        if (err.response.status === 429) return 'Too many requests. Please slow down.';
        return `Request failed (${err.response.status}).`;
    }
    return (err && err.message) || 'Network error.';
}

export default { namespaced, state, getters, mutations, actions };
